import Axios, { AxiosResponse } from 'axios';
import jwt_decode from 'jwt-decode';

import ApiHelper, { isUsableAuthFromApex } from '../../helpers/apiHelper';
import { store } from '../../app/store';
import { addAuthInfo, refreshToken } from '../../slices/auth';
import { appDebugLog, appDebugWarn } from '../../Util/appDebugLog';
import { NetworkAvailability } from '../../Util/NetworkAvailability';
import { logger } from '../../Util/Logger/ClientLogger';
import { runWithCrossTabLock } from '../../Util/crossTabLock';
import {
  publishSharedGatewayAuth,
  readSharedGatewayAuth,
  subscribeSharedGatewayAuth,
  SharedGatewayAuth,
} from '../../Util/sharedGatewayAuth';

const VoiceIQApiAxiosInstance = Axios.create();

/** Refresh this many ms before access token expiry so in-flight requests rarely see an expired JWT. */
const PROACTIVE_REFRESH_SKEW_MS = 20_000;
/** If computed delay is very small or negative, still wait a short beat before refreshing. */
const MIN_PROACTIVE_REFRESH_DELAY_MS = 5_000;
/** When the JWT is already expired (or past the proactive skew), avoid retrying every 5s — that hammered /connect/refresh and Apex. */
const PROACTIVE_REFRESH_EXPIRED_BACKOFF_MS = 60_000;

/** Serialize refresh so parallel 401s do not race the same refresh token. */
let refreshInFlight: Promise<string> | null = null;
let proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null;
let waitingForOnlineToRefresh = false;

type RefreshTokenResponse = {
  data?: {
    accessToken?: string;
    refreshToken?: string;
    accessTokenExpiresInSeconds?: number;
  };
};

export type AuthInfo = {
  UserId: string;
  Username: string;
  Email: string;
  FullName: string,
  OrgId: string,
  OrgName: string,
  RPAPIGW_URL: string,
  StatusCode: string,
  Status: number,
  AccessToken: string,
  RefreshToken: string,
  ExpiresIn: number,
  TokenType: string,
};

function resolveRtaspUrl(rpApigwUrl?: string): string | undefined {
  if (rpApigwUrl === process.env.REACT_APP_PROD_APIGW_URL) {
    return process.env.REACT_APP_PROD_RTASP_URL;
  }
  if (rpApigwUrl === process.env.REACT_APP_STAGING_APIGW_URL) {
    return process.env.REACT_APP_STAGING_RTASP_URL;
  }
  if (rpApigwUrl === process.env.REACT_APP_QA_APIGW_URL) {
    return process.env.REACT_APP_QA_RTASP_URL;
  }
  return process.env.REACT_APP_DEV_RTASP_URL;
}

function buildProcessedAuthInfo(authInfo: any) {
  const accessToken = authInfo?.AccessToken;
  const refreshTokenValue = authInfo?.RefreshToken;
  let userId = authInfo?.UserId;
  let tenantCode = authInfo?.TenantCode;

  if (accessToken) {
    try {
      const decoded: any = jwt_decode(accessToken);
      userId = userId || decoded?.sub;
      tenantCode = tenantCode || decoded?.website;
    } catch {
      // keep Salesforce-provided values if JWT decode fails
    }
  }

  return {
    ...authInfo,
    ...(accessToken ? { AccessToken: accessToken } : {}),
    ...(refreshTokenValue ? { RefreshToken: refreshTokenValue } : {}),
    ...(userId ? { userId } : {}),
    ...(tenantCode ? { tenantCode } : {}),
    rtaspUrl: resolveRtaspUrl(authInfo?.RPAPIGW_URL),
  };
}

function clearProactiveRefreshTimer() {
  if (proactiveRefreshTimer) {
    clearTimeout(proactiveRefreshTimer);
    proactiveRefreshTimer = null;
  }
}

function getJwtExpMs(accessToken: string): number | null {
  try {
    const decoded = jwt_decode(accessToken) as { exp?: number };
    if (typeof decoded?.exp === 'number') {
      return decoded.exp * 1000;
    }
  } catch {
    // ignore malformed token
  }
  return null;
}

/**
 * Schedules a silent refresh shortly before the current access token expires.
 * Call after login and after any successful token rotation (handled inside refreshAccessToken).
 * Pauses while offline / sleep so wake-up does not hammer /connect/refresh.
 */
export function scheduleProactiveAccessTokenRefresh() {
  clearProactiveRefreshTimer();
  const token = store.getState()?.auth?.authUser?.AccessToken;
  if (!token) {
    return;
  }

  if (!NetworkAvailability.isOnline()) {
    if (!waitingForOnlineToRefresh) {
      waitingForOnlineToRefresh = true;
      NetworkAvailability.onceOnline(() => {
        waitingForOnlineToRefresh = false;
        scheduleProactiveAccessTokenRefresh();
      });
    }
    return;
  }

  const expMs = getJwtExpMs(token);
  if (!expMs) {
    return;
  }
  const now = Date.now();
  const msUntilProactivePoint = expMs - now - PROACTIVE_REFRESH_SKEW_MS;
  const delay =
    msUntilProactivePoint <= 0
      ? PROACTIVE_REFRESH_EXPIRED_BACKOFF_MS
      : Math.max(MIN_PROACTIVE_REFRESH_DELAY_MS, msUntilProactivePoint);
  proactiveRefreshTimer = setTimeout(() => {
    proactiveRefreshTimer = null;
    if (!NetworkAvailability.isOnline()) {
      scheduleProactiveAccessTokenRefresh();
      return;
    }
    void refreshAccessToken().catch((err) => {
      if (NetworkAvailability.isNetworkError(err) || !NetworkAvailability.isOnline()) {
        scheduleProactiveAccessTokenRefresh();
        return;
      }
      const retryToken = store.getState()?.auth?.authUser?.AccessToken;
      const retryExp = retryToken ? getJwtExpMs(retryToken) : null;
      const stillHasTtl =
        retryExp != null && retryExp > Date.now() + PROACTIVE_REFRESH_SKEW_MS;
      if (stillHasTtl) {
        scheduleProactiveAccessTokenRefresh();
      } else {
        proactiveRefreshTimer = setTimeout(() => {
          proactiveRefreshTimer = null;
          scheduleProactiveAccessTokenRefresh();
        }, PROACTIVE_REFRESH_EXPIRED_BACKOFF_MS);
      }
    });
  }, delay);
}

function syncClientLoggerAccessToken(accessToken: string) {
  const auth = store.getState()?.auth?.authUser;
  logger.setUserContext({
    token: accessToken,
    ...(auth?.userId || auth?.UserId ? { userId: auth.userId || auth.UserId } : {}),
    ...(auth?.tenantCode || auth?.TenantCode
      ? { tenantId: auth.tenantCode || auth.TenantCode }
      : {}),
    ...(auth?.RPAPIGW_URL ? { baseUrl: auth.RPAPIGW_URL } : {}),
  });
}

function parseApexAuthJson(response: any): AuthInfo | null {
  if (!response?.success) {
    return null;
  }
  const raw = response?.returnValue?.runApex;
  if (typeof raw !== 'string') {
    return null;
  }
  try {
    return JSON.parse(raw) as AuthInfo;
  } catch {
    return null;
  }
}

/** Package callout to `/connect/refresh` via Apex; updates Platform Cache on success. */
function refreshAccessTokenViaPackage(): Promise<string> {
  return new Promise((resolve, reject) => {
    // @ts-ignore — Open CTI global in SF Lightning console
    const runApex = sforce?.opencti?.runApex;
    if (typeof runApex !== 'function') {
      reject(new Error('Salesforce Open CTI not available'));
      return;
    }
    runApex({
      apexClass: 'rocketphone.RPGetLoggedInUserInfo',
      methodName: 'refreshAccessToken',
      methodParams: '',
      callback: (response: any) => {
        const authInfo = parseApexAuthJson(response);
        if (!authInfo || !isUsableAuthFromApex(authInfo)) {
          console.error('Error refreshing token: Apex refreshAccessToken returned unusable auth', response);
          reject(new Error('Apex refreshAccessToken returned invalid or incomplete auth'));
          return;
        }
        const access = authInfo.AccessToken;
        const processedAuthInfo = buildProcessedAuthInfo(authInfo);
        store.dispatch(addAuthInfo(processedAuthInfo));
        VoiceIQApiAxiosInstance.defaults.headers.common['Authorization'] = `Bearer ${access}`;
        resolve(access);
      },
    });
  });
}

/** Browser → API GW refresh (used when Open CTI / package refresh is unavailable or fails). */
function refreshAccessTokenDirectGateway(): Promise<string> {
  const authUserData = store.getState()?.auth?.authUser;
  const accessToken = authUserData?.AccessToken;
  const currentRefreshToken =
    typeof authUserData?.RefreshToken === 'string' ? authUserData.RefreshToken.trim() : '';
  const orgId = authUserData?.OrgId;
  const baseURL = authUserData?.RPAPIGW_URL;

  if (!baseURL || !accessToken || !currentRefreshToken) {
    return Promise.reject(new Error('Missing credentials for direct gateway refresh'));
  }

  const requestParams = {
    orgId,
    accessToken,
    refreshToken: currentRefreshToken,
  };

  const config = {
    baseURL,
    headers: ApiHelper.createHeader(),
    url: '/connect/refresh',
    method: 'POST',
    data: requestParams,
  };

  return Axios.create()
    .request(config)
    .then((response) => {
      const refreshData = (response.data as RefreshTokenResponse)?.data;
      const newAccessToken = refreshData?.accessToken;
      const newRefreshToken = refreshData?.refreshToken;
      const accessTokenExpiresInSeconds = refreshData?.accessTokenExpiresInSeconds;

      if (!newAccessToken || !newRefreshToken) {
        throw new Error('Refresh endpoint returned invalid token payload');
      }

      store.dispatch(refreshToken({
        accessToken: newAccessToken,
        refreshToken: newRefreshToken,
        accessTokenExpiresInSeconds,
      }));

      return newAccessToken as string;
    });
}

VoiceIQApiAxiosInstance.interceptors.response.use(
  (response: AxiosResponse<any, any>) => {
    return ApiHelper.SuccessResponse(response);
  },
  (error) => {
    const originalRequest = error.config;

    // Network / DNS failures after sleep are not auth failures — do not refresh tokens.
    if (NetworkAvailability.isNetworkError(error) || !error.response) {
      return Promise.reject(error);
    }

    // Check if the error status is 401 (Unauthorized) and there's no token refresh in progress
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (!NetworkAvailability.isOnline()) {
        return Promise.reject(error);
      }
      originalRequest._retry = true;

      // Perform token refresh and retry the original request
      return refreshAccessToken().then((newToken) => {
        VoiceIQApiAxiosInstance.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
        const auth = store.getState()?.auth?.authUser;
        if (auth?.tenantCode) {
          originalRequest.headers['X-TENANT-CODE'] = auth.tenantCode;
        }
        return VoiceIQApiAxiosInstance.request(originalRequest);
      });
    }

    // Reject the promise for other errors or when token refresh is unsuccessful
    return Promise.reject(error);
  }
);

type AccessTokenRefreshedListener = () => void;
const accessTokenRefreshedListeners = new Set<AccessTokenRefreshedListener>();

/**
 * Subscribe to run after gateway Bearer rotation (/connect/refresh).
 * Listeners decide whether Beacon WebSocket must reconnect (WS JWT is separate from gateway Bearer).
 */
export function onAccessTokenRefreshed(listener: AccessTokenRefreshedListener): () => void {
  accessTokenRefreshedListeners.add(listener);
  return () => accessTokenRefreshedListeners.delete(listener);
}

function notifyAccessTokenRefreshed() {
  accessTokenRefreshedListeners.forEach((listener) => {
    try {
      listener();
    } catch (err) {
      appDebugWarn('onAccessTokenRefreshed listener failed', err);
    }
  });
}

function currentAuthUserId(): string | undefined {
  const auth = store.getState()?.auth?.authUser;
  return auth?.userId || auth?.UserId;
}

/**
 * Another tab already rotated the gateway tokens. Use that pair when it is newer
 * and still outside the proactive-refresh window, instead of spending the refresh token again.
 */
function adoptFresherSharedToken(): string | null {
  const shared = readSharedGatewayAuth();
  const local = store.getState()?.auth?.authUser;
  if (!shared || !local) return null;

  const localUserId = currentAuthUserId();
  if (shared.userId && localUserId && shared.userId !== localUserId) return null;

  const localExp = local.AccessToken ? getJwtExpMs(local.AccessToken) : null;
  const sharedStillUsable = shared.expMs > Date.now() + PROACTIVE_REFRESH_SKEW_MS;
  if (!sharedStillUsable) return null;

  if (shared.accessToken === local.AccessToken) {
    return shared.accessToken;
  }

  const sharedIsNewer = localExp == null || shared.expMs > localExp + 500;
  if (!sharedIsNewer) return null;

  store.dispatch(refreshToken({
    accessToken: shared.accessToken,
    refreshToken: shared.refreshToken,
    accessTokenExpiresInSeconds: shared.accessTokenExpiresInSeconds,
  }));
  logger.info('Gateway access token adopted from another tab', 'VoiceIQAPI', {
    expMs: shared.expMs,
  });
  return shared.accessToken;
}

function publishCurrentGatewayAuth(accessToken: string) {
  const auth = store.getState()?.auth?.authUser;
  const refreshTokenValue = typeof auth?.RefreshToken === 'string' ? auth.RefreshToken.trim() : '';
  const expMs = getJwtExpMs(accessToken);
  if (!refreshTokenValue || !expMs) return;
  publishSharedGatewayAuth({
    userId: currentAuthUserId(),
    accessToken,
    refreshToken: refreshTokenValue,
    accessTokenExpiresInSeconds: auth?.AccessTokenExpiresInSeconds,
    expMs,
  });
}

function applyGatewayAccessToken(newToken: string) {
  VoiceIQApiAxiosInstance.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
  syncClientLoggerAccessToken(newToken);
  scheduleProactiveAccessTokenRefresh();
  notifyAccessTokenRefreshed();
}

function rotateAccessToken(): Promise<string> {
  // Prefer package refresh (Apex callout + Platform Cache) when Open CTI is available; fall back to
  // direct gateway refresh from the browser, then getUserInfo full recovery.
  // @ts-ignore — Open CTI global in SF Lightning console
  const tryPackageFirst = typeof sforce !== 'undefined' && typeof sforce?.opencti?.runApex === 'function';

  return (tryPackageFirst
    ? refreshAccessTokenViaPackage()
    : Promise.reject(new Error('Open CTI unavailable')))
    .catch((packageErr) => {
      appDebugWarn('Package token refresh skipped or failed, trying direct gateway:', packageErr);
      return refreshAccessTokenDirectGateway();
    })
    .catch((directErr) => {
      appDebugWarn('Error refreshing token (direct gateway):', directErr);
      return refreshTokenViaSalesforceApex();
    });
}

/** Refresh gateway Bearer token (one refresh across tabs; other tabs wait and reuse it). */
export function refreshAccessToken(): Promise<string> {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  if (!NetworkAvailability.isOnline()) {
    return Promise.reject(new Error('Network unavailable — cannot refresh access token'));
  }

  const tokenBeforeRefresh = store.getState()?.auth?.authUser?.AccessToken;

  refreshInFlight = runWithCrossTabLock('rp-gateway-auth-refresh', async () => {
    const reused = adoptFresherSharedToken();
    if (reused) return reused;
    return rotateAccessToken();
  })
    .then((newToken) => {
      applyGatewayAccessToken(newToken);
      if (newToken && newToken !== tokenBeforeRefresh) {
        publishCurrentGatewayAuth(newToken);
      }
      appDebugLog('[WebSocketAuth] refreshAccessToken succeeded');
      return newToken;
    })
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
}

subscribeSharedGatewayAuth((shared: SharedGatewayAuth) => {
  const before = store.getState()?.auth?.authUser?.AccessToken;
  const adopted = adoptFresherSharedToken();
  if (!adopted || adopted === before) return;
  if (shared.accessToken !== adopted) return;
  applyGatewayAccessToken(adopted);
  appDebugLog('[WebSocketAuth] adopted gateway token published by another tab');
});

/**
 * Last-resort recovery: re-fetch tokens from Salesforce via Open CTI (`getUserInfo`) after package refresh
 * and direct gateway refresh both fail (or Open CTI is unavailable for the earlier steps).
 * Must return a Promise — fire-and-forget runApex left callers with no new token and always threw.
 */
function refreshTokenViaSalesforceApex(): Promise<string> {
  return new Promise((resolve, reject) => {
    // @ts-ignore — Open CTI global in SF Lightning console
    const runApex = sforce?.opencti?.runApex;
    if (typeof runApex !== 'function') {
      reject(new Error('Salesforce Open CTI not available for token recovery'));
      return;
    }
    runApex({
      apexClass: 'rocketphone.RPGetLoggedInUserInfo',
      methodName: 'getUserInfo',
      methodParams: '',
      callback: (response: any) => {
        if (response.success) {
          const authInfo = parseApexAuthJson(response);
          if (!authInfo || !isUsableAuthFromApex(authInfo)) {
            console.error('Error refreshing token: Apex returned unusable auth payload', response);
            reject(new Error('Apex getUserInfo returned invalid or incomplete auth'));
            return;
          }
          const access = authInfo.AccessToken;

          const processedAuthInfo = buildProcessedAuthInfo(authInfo);
          store.dispatch(addAuthInfo(processedAuthInfo));
          VoiceIQApiAxiosInstance.defaults.headers.common['Authorization'] = `Bearer ${access}`;
          resolve(access);
        } else {
          console.error('Error refreshing token via Apex:', response);
          reject(new Error('Apex getUserInfo was not successful'));
        }
      }
    });
  });
}

export default VoiceIQApiAxiosInstance;
